import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import api from 'api';
import PopNotificationWithUndo from 'components/PopNotificationWithUndo';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import useUndoActions from 'hooks/useUndoActions';
import { getArrayLength } from 'utils';
import {
  PendingActionsDetailResponse,
  ReadablePendingActionType,
} from '../types';

type UseMarkPendingActionsAsReadParams = {
  dealRoomId: string;
  dealName: string;
  data: PendingActionsDetailResponse;
  setData: Dispatch<SetStateAction<PendingActionsDetailResponse>>;
  // Keeps the table row's pending actions pills in sync with the drawer.
  onCountChange?: (type: ReadablePendingActionType, count: number) => void;
};

type RemovedItems = PendingActionsDetailResponse[ReadablePendingActionType];

const MARKED_AS_READ_KEY: Record<ReadablePendingActionType, string> = {
  messages: 'messagesMarkedAsRead',
  leadNotes: 'leadNotesMarkedAsRead',
  dealNotes: 'dealNotesMarkedAsRead',
};

const READABLE_TYPES = Object.keys(
  MARKED_AS_READ_KEY,
) as ReadablePendingActionType[];

const useMarkPendingActionsAsRead = (
  params: UseMarkPendingActionsAsReadParams,
) => {
  const { dealRoomId, dealName, data, setData, onCountChange } = params;

  const { t } = useTranslation();

  const { popServerError } = useContext(PopNotificationsContext);
  const { refetchCurrentUser } = useContext(CurrentUserContext);

  // One independent undo timer per readable section so several sections can be
  // cleared (and individually undone) within the same undo window.
  const messagesUndo = useUndoActions();
  const leadNotesUndo = useUndoActions();
  const dealNotesUndo = useUndoActions();

  const undoActionsByType = useMemo(
    () => ({
      messages: messagesUndo,
      leadNotes: leadNotesUndo,
      dealNotes: dealNotesUndo,
    }),
    [dealNotesUndo, leadNotesUndo, messagesUndo],
  );

  // Snapshot of the items removed per section, kept so undo can restore them.
  const removedItemsRef = useRef<
    Partial<Record<ReadablePendingActionType, RemovedItems>>
  >({});

  const markAsRead = useCallback(
    (type: ReadablePendingActionType) => async () => {
      const removedItems = data?.[type];
      const removedCount = getArrayLength(removedItems);

      if (!removedCount) return;

      // Optimistically clear the section from the drawer and the table row.
      removedItemsRef.current[type] = removedItems;
      setData((prev) => ({ ...prev, [type]: [] }));
      onCountChange?.(type, 0);
      undoActionsByType[type].setUndoTimeout(() => {
        removedItemsRef.current[type] = undefined;

        // Only once undo is no longer possible, so an undone action does not
        // pull already-stale unread sessions back in.
        if (type === 'messages') refetchCurrentUser();
      });

      try {
        await api.portfolio.admin.markBossModeSuggestedActionsAsRead(
          dealRoomId,
          type,
        );
      } catch (e) {
        // Roll back the optimistic removal on failure.
        setData((prev) => ({ ...prev, [type]: removedItems }));
        onCountChange?.(type, removedCount);
        removedItemsRef.current[type] = undefined;
        undoActionsByType[type].clearUndoTimeout();
        popServerError(e);
      }
    },
    [
      data,
      dealRoomId,
      onCountChange,
      popServerError,
      refetchCurrentUser,
      setData,
      undoActionsByType,
    ],
  );

  const undo = useCallback(
    (type: ReadablePendingActionType) => async () => {
      const removedItems = removedItemsRef.current[type];

      undoActionsByType[type].clearUndoTimeout();
      setData((prev) => ({ ...prev, [type]: removedItems }));
      onCountChange?.(type, getArrayLength(removedItems));
      removedItemsRef.current[type] = undefined;

      try {
        await api.portfolio.admin.restoreBossModeSuggestedActions(
          dealRoomId,
          type,
        );
      } catch (e) {
        popServerError(e);
      }
    },
    [dealRoomId, onCountChange, popServerError, setData, undoActionsByType],
  );

  const undoNotifications = (
    <>
      {READABLE_TYPES.map((type) => (
        <PopNotificationWithUndo
          key={type}
          isUndoAvailable={undoActionsByType[type].isUndoAvailable}
          onUndo={undo(type)}
        >
          <p>{t(MARKED_AS_READ_KEY[type], { dealName })}</p>
        </PopNotificationWithUndo>
      ))}
    </>
  );

  return { markAsRead, undoNotifications };
};

export default useMarkPendingActionsAsRead;
